"use client";

import {
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type ReactNode,
} from "react";
import type { OAuthProvider } from "@/lib/oauth/config";
import type { Locale } from "@/lib/i18n";
import type { OAuthMode } from "@/lib/oauth/state";
import type { UserGender } from "@/lib/user-gender";
import { AppleLogo, GoogleLogo, KakaoLogo, NaverLogo } from "./brand-logos";
import { isNativeApp, nativeHaptic, nativeLogin } from "@/lib/native-bridge";

type EnabledProviders = Record<OAuthProvider, boolean>;

type Props = {
  mode: OAuthMode;
  redirectTo: string;
  enabled: EnabledProviders;
  agreedTerms?: boolean;
  agreedPrivacy?: boolean;
  gender?: UserGender | "";
  locale?: Locale;
};

// 인앱에서 네이티브 SDK 로 처리할 provider. naver 는 네이티브 SDK 가 없고
// 웹뷰 OAuth 가 허용되므로(구글과 달리) 기존 웹 플로우를 그대로 쓴다.
type NativeProvider = "google" | "kakao" | "apple";

function isNativeProvider(provider: OAuthProvider): provider is NativeProvider {
  return provider === "google" || provider === "kakao" || provider === "apple";
}

const baseButtonClassName =
  "flex h-12 w-full items-center gap-3 rounded-xl pl-4 pr-5 text-sm font-medium transition active:scale-[.98] active:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-900 disabled:cursor-not-allowed disabled:opacity-50";

const OAUTH_BUTTONS: Array<{
  provider: OAuthProvider;
  label: string;
  ariaLabel: string;
  className: string;
  style?: CSSProperties;
  Logo: (props: { size?: number | string }) => ReactNode;
}> = [
  {
    provider: "google",
    label: "Google로 계속하기",
    ariaLabel: "Google 계정으로 계속하기",
    className: `${baseButtonClassName} border border-slate-300 bg-white text-slate-700`,
    Logo: GoogleLogo,
  },
  {
    provider: "kakao",
    label: "카카오로 계속하기",
    ariaLabel: "카카오로 계속하기",
    className: baseButtonClassName,
    style: { backgroundColor: "#FEE500", color: "rgba(0,0,0,0.85)" },
    Logo: KakaoLogo,
  },
  {
    provider: "naver",
    label: "네이버로 계속하기",
    ariaLabel: "네이버로 계속하기",
    className: `${baseButtonClassName} bg-[#03C75A] text-white`,
    Logo: NaverLogo,
  },
  {
    provider: "apple",
    label: "Apple로 계속하기",
    ariaLabel: "Apple 계정으로 계속하기",
    className: `${baseButtonClassName} bg-black text-white`,
    Logo: AppleLogo,
  },
];

function buildStartHref(
  provider: OAuthProvider,
  mode: OAuthMode,
  redirectTo: string,
  agreedTerms?: boolean,
  agreedPrivacy?: boolean,
  gender?: UserGender | "",
  locale: Locale = "ko",
): string {
  const params = new URLSearchParams();
  params.set("mode", mode);
  if (redirectTo) params.set("redirectTo", redirectTo);
  if (agreedTerms) params.set("agreedTerms", "1");
  if (agreedPrivacy) params.set("agreedPrivacy", "1");
  if (gender) params.set("gender", gender);
  params.set("locale", locale);
  return `/api/auth/oauth/${provider}/start?${params.toString()}`;
}

function localizedProviderCopy(provider: OAuthProvider, locale: Locale): { label: string; ariaLabel: string } {
  if (locale === "ko") {
    const item = OAUTH_BUTTONS.find((button) => button.provider === provider);
    return { label: item?.label ?? provider, ariaLabel: item?.ariaLabel ?? provider };
  }
  const names = { google: "Google", kakao: "Kakao", naver: "Naver", apple: "Apple" } as const;
  const name = names[provider];
  return { label: `Continue with ${name}`, ariaLabel: `Continue with your ${name} account` };
}

export default function SocialLoginButtons({
  mode,
  redirectTo,
  enabled,
  agreedTerms,
  agreedPrivacy,
  gender,
  locale = "ko",
}: Props) {
  // 결정적 주의 #1: 인앱에서 Google OAuth 는 웹뷰에서 막힌다 → 네이티브 SDK 로그인.
  const inApp = useSyncExternalStore(
    () => () => undefined,
    () => isNativeApp(),
    () => false,
  );
  const [pending, setPending] = useState<OAuthProvider | null>(null);
  const [error, setError] = useState<string | null>(null);

  const visibleButtons = OAUTH_BUTTONS.filter(({ provider }) => enabled[provider]);
  if (visibleButtons.length === 0) return null;

  const needAgreement = mode === "signup";
  const canProceed = !needAgreement || (agreedTerms && agreedPrivacy);
  const disabledTitle = !canProceed
    ? locale === "en"
      ? "Agree to the Terms of Service and Privacy Policy first."
      : "이용약관·개인정보처리방침 동의 후 이용할 수 있어요."
    : undefined;

  async function handleNativeLogin(provider: NativeProvider) {
    if (pending) return;
    setError(null);
    setPending(provider);
    void nativeHaptic("light");
    try {
      const result = await nativeLogin(provider, { locale, redirectTo });
      if (result.ok) {
        // 기존 계정 — 세션 쿠키 발급됨. 전체 새로고침으로 세션 반영.
        window.location.assign(redirectTo || "/");
        return;
      }
      if (result.reason === "needs_consent") {
        // 신규 가입/연결 — nativeLogin 이 이미 동의 페이지로 이동시킴.
        return;
      }
      if (result.reason === "cancelled") {
        setPending(null);
        return;
      }
      setError(locale === "en" ? "Sign-in failed. Please try again." : "로그인 처리 중 문제가 생겼어요. 다시 시도해 주세요.");
      setPending(null);
    } catch {
      setError(locale === "en" ? "Sign-in failed. Please try again." : "로그인 처리 중 문제가 생겼어요. 다시 시도해 주세요.");
      setPending(null);
    }
  }

  return (
    <section aria-label={locale === "en" ? "Continue with a social account" : "소셜 계정으로 계속하기"} className="flex flex-col gap-2">
      {visibleButtons.map(({ provider, className, style, Logo }) => {
        const { label, ariaLabel } = localizedProviderCopy(provider, locale);
        // 동의 미완료(signup) → 비활성 버튼.
        if (!canProceed) {
          return (
            <button
              key={provider}
              type="button"
              disabled
              aria-label={ariaLabel}
              title={disabledTitle}
              className={className}
              style={style}
            >
              <Logo size={20} />
              <span>{label}</span>
            </button>
          );
        }

        // 인앱 + 네이티브 지원 provider → 네이티브 SDK 로그인.
        if (inApp && isNativeProvider(provider)) {
          return (
            <button
              key={provider}
              type="button"
              onClick={() => handleNativeLogin(provider)}
              disabled={pending !== null}
              aria-label={ariaLabel}
              className={className}
              style={style}
            >
              <Logo size={20} />
              <span>{pending === provider ? (locale === "en" ? "Signing in…" : "로그인 중…") : label}</span>
            </button>
          );
        }

        // 웹(또는 인앱 naver) → 기존 OAuth 시작 링크.
        return (
          <a
            key={provider}
            href={buildStartHref(
              provider,
              mode,
              redirectTo,
              agreedTerms,
              agreedPrivacy,
              gender,
              locale,
            )}
            aria-label={ariaLabel}
            className={className}
            style={style}
          >
            <Logo size={20} />
            <span>{label}</span>
          </a>
        );
      })}
      {error ? (
        <p role="alert" className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </p>
      ) : null}
    </section>
  );
}
